// =============================================================================
// Justificar desvio
//
// Regra que a tela precisa comunicar sem ambiguidade (AF-01 §7, Especificação
// §7): justificar NÃO muda o valor orçado. O desvio continua aparecendo no
// indicador até uma revisão orçamentária formal. Se a tela deixar isso implícito,
// o gestor justifica achando que "resolveu" e descobre o contrário no fechamento.
// =============================================================================

import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { registrarJustificativa, FalhaApi, type LinhaMes } from "../lib/api.ts";
import { competenciaLonga, moeda } from "../lib/formato.ts";
import { Modal } from "../componentes/Modal.tsx";

const MOTIVOS = [
  {
    valor: "upgrade",
    rotulo: "Upgrade de plano",
    efeito:
      "O orçado permanece o mesmo até uma revisão orçamentária formal. O Financeiro é notificado para ajustar os lançamentos futuros no Conta Azul.",
  },
  {
    valor: "renegociacao",
    rotulo: "Renegociação de contrato",
    efeito:
      "Mesma lógica do upgrade: aguarda decisão formal. O Financeiro é notificado para ajustar os lançamentos futuros.",
  },
  {
    valor: "uso_pontual",
    rotulo: "Uso pontual acima do normal",
    efeito:
      "Cria uma tolerância temporária pelo prazo informado. O orçado não muda; o alerta fica suspenso e volta a disparar automaticamente depois, se o gasto continuar elevado.",
  },
  {
    valor: "outro",
    rotulo: "Outro",
    efeito: "Registro livre. Encaminhado ao Financeiro para avaliação caso a caso.",
  },
] as const;

export function ModalJustificativa({
  linha,
  onFechar,
  onSalvo,
}: {
  /** Justificar é sobre a linha de um mês — ver ModalLancamentos. */
  linha: LinhaMes & { linha_id: string };
  onFechar: () => void;
  onSalvo: () => void;
}) {
  const [motivo, setMotivo] = useState<string>(MOTIVOS[0].valor);
  const [texto, setTexto] = useState("");
  const [duracao, setDuracao] = useState(2);
  const primeiroCampo = useRef<HTMLSelectElement>(null);

  useEffect(() => primeiroCampo.current?.focus(), []);

  const salvar = useMutation({
    mutationFn: () =>
      registrarJustificativa({
        linha_id: linha.linha_id,
        competencia: linha.competencia,
        motivo,
        texto: texto.trim(),
        duracao_meses: motivo === "uso_pontual" ? duracao : null,
      }),
    onSuccess: onSalvo,
  });

  const efeito = MOTIVOS.find((m) => m.valor === motivo)?.efeito ?? "";
  const desvio = BigInt(linha.realizado_centavos) - BigInt(linha.orcado_centavos);

  return (
    <Modal titulo="Justificar desvio" onFechar={onFechar}>
      <div className="rounded-lg border border-risco-linha bg-risco-bg px-4 py-3">
        <p className="text-[13px] font-semibold text-risco-tinta">Gasto acima do previsto</p>
        <p className="mt-0.5 text-[12.5px] text-tinta-media">
          {linha.categoria_nome} · {linha.centro_custo_nome} · {competenciaLonga(linha.competencia)}
        </p>
        <p className="num mt-2 font-display text-[20px] font-bold text-risco-tinta">
          {moeda(linha.realizado_centavos)}
        </p>
        <p className="num text-[12.5px] text-tinta-media">
          {moeda(desvio.toString())} acima do teto de {moeda(linha.orcado_centavos)}
        </p>
      </div>

      <label className="mt-4 block">
        <span className="text-[12px] font-semibold text-tinta-media">Motivo</span>
        <select
          ref={primeiroCampo}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          className="mt-1 w-full rounded-lg border border-linha bg-superficie px-3 py-2 text-[14px]"
        >
          {MOTIVOS.map((m) => (
            <option key={m.valor} value={m.valor}>
              {m.rotulo}
            </option>
          ))}
        </select>
      </label>

      {motivo === "uso_pontual" && (
        <label className="mt-3 block">
          <span className="text-[12px] font-semibold text-tinta-media">
            Duração esperada do pico
          </span>
          <select
            value={duracao}
            onChange={(e) => setDuracao(Number(e.target.value))}
            className="mt-1 w-full rounded-lg border border-linha bg-superficie px-3 py-2 text-[14px]"
          >
            {[1, 2, 3, 6].map((n) => (
              <option key={n} value={n}>
                {n} {n === 1 ? "mês" : "meses"}
              </option>
            ))}
          </select>
        </label>
      )}

      <label className="mt-3 block">
        <span className="text-[12px] font-semibold text-tinta-media">Justificativa</span>
        <textarea
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          rows={3}
          placeholder="O que explica esse gasto acima do previsto?"
          className="mt-1 w-full resize-y rounded-lg border border-linha bg-superficie px-3 py-2 text-[14px]"
        />
      </label>

      <p className="mt-3 rounded-lg border border-linha bg-superficie-2 px-3 py-2 text-[12.5px] text-tinta-media">
        {efeito}
      </p>

      {salvar.isError && (
        <p className="mt-3 rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-[12.5px] text-risco-tinta">
          {(salvar.error as FalhaApi).message}
        </p>
      )}

      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={onFechar}
          className="rounded-lg border border-linha px-4 py-2 text-[13px] font-semibold hover:bg-superficie-2"
        >
          Cancelar
        </button>
        <button
          type="button"
          disabled={texto.trim().length < 10 || salvar.isPending}
          onClick={() => salvar.mutate()}
          className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul disabled:cursor-not-allowed disabled:opacity-45"
        >
          {salvar.isPending ? "Registrando…" : "Registrar justificativa"}
        </button>
      </div>
      {texto.trim().length < 10 && (
        <p className="mt-1.5 text-right text-[11.5px] text-tinta-fraca">
          Escreva ao menos uma frase — o Financeiro lê isto para decidir.
        </p>
      )}
    </Modal>
  );
}
